import { Editor } from "@tiptap/core";
import { Component, createRef } from "react";

import SearchModal from "@apps/search/search";
import { classNames } from "@helpers/css";
import { getTiptapSchema } from "@helpers/helpers";

import EmbedUrlDialog from "./embeds/embed-url-dialog";
import GiphyDialog from "./embeds/giphy-dialog";

export const giphyCommandItem = {
  id: "giphy",
  title: "Giphy",
  command: ({
    editor,
    range,
    props,
  }: {
    editor: Editor;
    range: any;
    props: any;
  }) => {
    editor
      .chain()
      .focus()
      .deleteRange(range)
      .setImage({
        src: props.gif.images.original.webp,
        alt: props.gif.title,
        title: props.gif.title,
      })
      .run();
  },
};

export const embedUrlCommandItem = {
  id: "embedUrl",
  title: "Embed URL",
};

export const githubCommandItem = {
  ...embedUrlCommandItem,
  title: "Github",
};

export const clickupCommandItem = {
  ...embedUrlCommandItem,
  title: "ClickUp",
};

export const hubspotCommandItem = {
  ...embedUrlCommandItem,
  title: "Hubspot deal",
};

export const salesforceCommandItem = {
  ...embedUrlCommandItem,
  title: "Salesforce opportunity",
};

export const jiraCommandItem = {
  ...embedUrlCommandItem,
  title: "Jira issue",
};

export const iframeCommandItem = {
  ...embedUrlCommandItem,
  title: "Iframe",
};

export const youtubeCommandItem = {
  ...embedUrlCommandItem,
  title: "Youtube",
};

export const figmaCommandItem = {
  ...embedUrlCommandItem,
  title: "Figma",
};

export const loomCommandItem = {
  ...embedUrlCommandItem,
  title: "Loom",
};

export const miroCommandItem = {
  ...embedUrlCommandItem,
  title: "Miro",
};

export const vimeoCommandItem = {
  ...embedUrlCommandItem,
  title: "Vimeo",
};

export const asanaCommandItem = {
  ...embedUrlCommandItem,
  title: "Asana",
};

export const explorerCommandItem = {
  id: "explorer",
  title: "Explorer",
};

export const searchCommandItem = {
  id: "search",
  keywords: "find",
  title: "Search",
  searchFilters: {},
  command: ({ editor }: { editor: Editor }) => {
    editor.chain().focus().run();
  },
};

export const findActionItemsCommandItem = {
  id: "find-action-items",
  keywords: "search",
  title: "Find an action item",
};

export const findGoalsCommandItem = {
  id: "find-goals",
  keywords: "search",
  title: "",
};

export const findRecognitionCommandItem = {
  id: "find-recognition",
  keywords: "search",
  title: "",
};

export const findHubspotDealCommandItem = {
  id: "find-hubspot-deal",
  keywords: "search",
  title: "Find a Hubspot deal",
};

export const findJiraIssueCommandItem = {
  id: "find-jira-issue",
  keywords: "search",
  title: "Find a Jira issue",
};

export const findGithubIssueCommandItem = {
  id: "find-github-issue",
  keywords: "search",
  title: "Find a Github issue",
};

const defaultIndex = 1;

type Props = {
  items: any[];
  editor: any;
  command: any;
  range: any;
};

export class CommandList extends Component<Props> {
  state = {
    selectedIndex: defaultIndex,
    showDialog: null,
  };
  containerRef;

  constructor(props: Props) {
    super(props);
    this.containerRef = createRef<HTMLDivElement>();
  }

  componentDidUpdate(oldProps: any, oldState: any) {
    if (this.props.items !== oldProps.items) {
      this.setState({ selectedIndex: defaultIndex });
    }
    if (
      this.state.selectedIndex !== oldState.selectedIndex &&
      this.containerRef.current
    ) {
      const child = this.containerRef.current.children.item(
        this.state.selectedIndex,
      ) as HTMLDivElement;
      const parentHeight = this.containerRef.current.offsetHeight;
      const parentScrollTop = this.containerRef.current.scrollTop;
      if (!child) return;
      const childTop = child.offsetTop;
      const childHeight = child.offsetHeight;

      if (this.state.selectedIndex === 1) {
        // make title visible
        this.containerRef.current.scrollTo(0, 0);
      } else if (childTop + childHeight >= parentScrollTop + parentHeight) {
        const y = childTop + childHeight - parentHeight;
        this.containerRef.current.scrollTo(0, y);
      } else if (childTop < parentScrollTop) {
        this.containerRef.current.scrollTo(0, childTop);
      }
    }
  }

  onKeyDown({ event }: any) {
    if (event.key === "ArrowUp") {
      this.upHandler();
      return true;
    }
    if (event.key === "ArrowDown") {
      this.downHandler();
      return true;
    }
    if (event.key === "Enter") {
      if (this.props.items.length > 0) {
        this.enterHandler();
        return true;
      }
      return false;
    }
    if (event.key === "Tab") {
      this.enterHandler();
      return true;
    }
    return false;
  }

  upHandler() {
    let newIndex = this.state.selectedIndex - 1;
    if (newIndex > 0 && this.props.items[newIndex].subItems) {
      newIndex = newIndex - 1;
    }
    if (newIndex > 0) {
      this.setState({ selectedIndex: newIndex });
    }
  }

  downHandler() {
    let newIndex = this.state.selectedIndex + 1;
    if (
      newIndex < this.props.items.length &&
      this.props.items[newIndex].subItems
    ) {
      newIndex = newIndex + 1;
    }
    if (newIndex < this.props.items.length) {
      this.setState({ selectedIndex: newIndex });
    }
  }

  enterHandler() {
    this.selectItem(this.state.selectedIndex);
  }

  selectItem(index: number) {
    const item = this.props.items[index];
    if (item) {
      const dialogIds = [
        giphyCommandItem.id,
        iframeCommandItem.id,
        youtubeCommandItem.id,
        figmaCommandItem.id,
        loomCommandItem.id,
        miroCommandItem.id,
        vimeoCommandItem.id,
        asanaCommandItem.id,
        embedUrlCommandItem.id,
        searchCommandItem.id,
        findActionItemsCommandItem.id,
        findGoalsCommandItem.id,
        findRecognitionCommandItem.id,
        findHubspotDealCommandItem.id,
        findJiraIssueCommandItem.id,
        findGithubIssueCommandItem.id,
      ];
      if (dialogIds.includes(item.id)) {
        return this.setState({ showDialog: item.id });
      }
      // necessary though to let the tooltip/command dialog to close
      // allows to bring focus into artifact field
      setTimeout(() => {
        this.props.command(item);
      }, 150); // timeout number is made up
    }
  }

  handleClickSearchResult = (result: any) => {
    this.setState({ showDialog: false });
    const content = getTiptapSchema(result) as any;
    if (content) {
      this.props.editor
        .chain()
        .focus()
        .deleteRange(this.props.range)
        .insertContent(content)
        .run();
    } else {
      throw new Error(`Missing result: ${result.__typename}`);
    }
  };

  handleCloseDialog = () => {
    this.setState({ showDialog: false });
  };

  render() {
    const { items } = this.props;
    const { showDialog } = this.state;
    const matchingCommand = items.find(({ id }) => id === showDialog);
    // const showSearchDialog
    return (
      <>
        {matchingCommand?.searchFilters && (
          <SearchModal
            initialFilters={{
              meetingGroupId:
                this.props.editor?.options?.editorProps?.meetingGroupId || null,
              ...matchingCommand.searchFilters,
            }}
            onClickResult={this.handleClickSearchResult}
            onClose={this.handleCloseDialog}
          />
        )}
        {showDialog === giphyCommandItem.id && (
          <GiphyDialog
            command={this.props.command}
            item={giphyCommandItem}
            onClose={this.handleCloseDialog}
          />
        )}
        {showDialog === embedUrlCommandItem.id && (
          <EmbedUrlDialog
            range={this.props.range}
            editor={this.props.editor}
            onClose={this.handleCloseDialog}
          />
        )}

        {!showDialog && items.length > 0 && (
          <div
            className="relative rounded bg-white border text-sm shadow js-wysiwyg-command overflow-y-auto max-h-72"
            aria-label="wysiwyg-commands"
            ref={this.containerRef}
          >
            {items.map((item, index: number) => {
              return item.subItems ? (
                <div
                  key={String(item.id + item.title)}
                  className="pl-3 pr-2 py-0.5 pt-1 text-2xs text-gray-500 uppercase tracking-tight font-medium border-b bg-gray-50"
                >
                  {item.title}
                </div>
              ) : (
                <button
                  className={classNames(
                    `block w-48 text-left pl-3 pr-2 py-1.5 text-gray-800 hover:bg-gray-50 hover:text-blue-600 border-b font-medium tracking-tight`,
                    index === this.state.selectedIndex &&
                      "text-blue-600 bg-gray-100",
                  )}
                  key={String(item.id + item.title)}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.selectItem(index);
                  }}
                >
                  {item.element || item.title}
                </button>
              );
            })}
          </div>
        )}
      </>
    );
  }
}
